import React, { useState, useRef, useEffect } from 'react';
import { useLayers } from '@context';
import { useSettings } from '@context';
import {
Stack,
Option,
Select,
Button,
Typography,
Slider,
Box,
Divider,
} from '@mui/joy';
import SldStyleParser from 'geostyler-sld-parser';
import { ColorMapEditor } from '@renci/apsviz-geostyler';
import { restoreColorMapType } from '@utils/map-utils';
import _cloneDeep from 'lodash/cloneDeep';
import { getFloatNumberFromLabel } from './utils';
import { feetToMeters, knotsToMps, mphToMps } from '@utils/map-utils';

const MAXELE = 'maxele';
const MAXWVEL = 'maxwvel';
const SWAN = 'swan';

export const StyleEditor = () => {
    const { defaultModelLayers } = useLayers();
    const { mapStyle, layerOpacity, unitsType, speedType } = useSettings();

    const [colormap, setColormap] = useState();
    const [style, setStyle] = useState();
    const productList = [];
    const sldParser = new SldStyleParser();
    const isInternalUpdate = useRef(false);

    // not sure if this is the right thing to do,
    // but just retrieving all the raster layer types
    // currently in the layer list
    const getLoadedProductNames = () => {
        defaultModelLayers.forEach(el => {
        if (
            !el.properties.product_type.includes('obs') &&
            !el.properties.product_type.includes('_ani') &&
            !productList.includes(el.properties.product_name)
        ) {
            productList.push(el.properties.product_name);
        }
        });
    };
    if (defaultModelLayers) getLoadedProductNames();

    // update style if speed units change
    useEffect(() => {
        if (!colormap) return;

        // Only update if the colormap contains speed values
        const isSpeedLayer = colormap.colorMapEntries?.[0]?.label?.includes("kn") 
                            || colormap.colorMapEntries?.[0]?.label?.includes("mph");

        if (!isSpeedLayer) return;

        const newColorMap = _cloneDeep(colormap);

        // Determine label unit
        const labelUnit = speedType.current === "mph" ? " mph" : " kn";

        newColorMap.colorMapEntries = newColorMap.colorMapEntries.map(entry => {
            const value = parseFloat(getFloatNumberFromLabel(entry.label, 2));
            const quantity = speedType.current === "mph" ? mphToMps(value) : knotsToMps(value);

            return {
            ...entry,
            quantity,
            label: `${value.toFixed(2)}${labelUnit}`,
            };
        });

        setColormap(newColorMap);
    }, [speedType.current]);

    // handles user selection of layer type to style
    const handleProductChange = (event, value) => {
        let tmpStyle = '';
        if (value === "Maximum Wind Speed") tmpStyle = mapStyle.maxwvel.current;
        else if (value === "Maximum Significant Wave Height") tmpStyle = mapStyle.swan.current;
        else tmpStyle = mapStyle.maxele.current;

        setStyle(tmpStyle);

        sldParser.readStyle(tmpStyle).then(geoStyle => {
        setColormap(geoStyle.output.rules[0].symbolizers[0].colorMap);
        });
    };

  // utility to generate numeric range (keep numbers, no toFixed)
    const getRangeList = (startingNumber, endingNumber, maxNumber) =>
        Array.from({ length: maxNumber }, (_, i) =>
            Math.round(
            (startingNumber +
                (i * (endingNumber - startingNumber)) / (maxNumber - 1)) * 100
            ) / 100
    );

    // handles any change to the colormap properties provided
    // by the geostyler package component - ColorMapEdit
    // editable properties include colormap type,
    // number of classes and color ramp
    const onColorMapChange = (value) => {
        if (!colormap) return;

        if (isInternalUpdate.current) return;

        // handle type change first
        if (value.type && value.type !== colormap.type) {
            const newColorMap = {
                ...colormap,
                type: value.type,
                colorMapEntries: colormap.colorMapEntries.map(e => ({ ...e })),
            };
            setColormap(newColorMap);
            return; // exit early to force re-render
        }

        // get values changed and update the style accordingly
        const newColorMap = _cloneDeep(colormap);

        // Determine label unit
        let labelUnit = colormap.colorMapEntries?.[0]?.label?.split("").reverse().join("").split(" ")[0] || "";
        if (labelUnit.length > 1) labelUnit = labelUnit.split("").reverse().join("");

        const oldLength = colormap.colorMapEntries?.length || 0;
        const newLength = value.colorMapEntries?.length || 0;

        // check if number of classes changed
        if (oldLength !== newLength) {
            // changed number of classes, so must rebuild the quantity and label
            // values with the colormap range currently defined.
            const topRange = parseFloat(getFloatNumberFromLabel(colormap.colorMapEntries[oldLength - 1].label, 2));
            const range = [parseFloat(colormap.colorMapEntries[0].quantity), topRange];
            const newRangeList = getRangeList(range[0], range[1], newLength);

            newColorMap.colorMapEntries = value.colorMapEntries.map((entry, index) => {
                const newEntry = { ...entry };

                // need to change the quantities to metric values if using imperial 
                // units, because GeoServer only recognizes the native unit of measurement
                // for a layer
                if (unitsType.current === "imperial") {
                if (["kn", "mph"].includes(labelUnit)) {
                    newEntry.quantity = speedType.current === "mph"
                    ? mphToMps(newRangeList[index])
                    : knotsToMps(newRangeList[index]);
                } else {
                    newEntry.quantity = feetToMeters(newRangeList[index]);
                }
                } else {
                newEntry.quantity = newRangeList[index]; // keep numeric
                }

                newEntry.label = `${newRangeList[index].toFixed(2)} ${labelUnit}`; // format for label only
                return newEntry;
            });
        } else {
            // same number of classes
            newColorMap.colorMapEntries = value.colorMapEntries.map((entry, index, arr) => {
                const newEntry = { ...entry };

                if (index === arr.length - 1 && newEntry.label?.includes(">=")) {
                const quantity = parseFloat(getFloatNumberFromLabel(newEntry.label, 2));
                const labelParts = newEntry.label.split(" ");
                newEntry.quantity = quantity;
                if (labelParts.length >= 3) {
                    newEntry.label = `${quantity.toFixed(2)} ${labelParts[2]}`;
                }
                }

                return newEntry;
            });
        }

        isInternalUpdate.current = true;
        setColormap(newColorMap);
        setTimeout(() => { isInternalUpdate.current = false; }, 0);
        return;
    };

    // Save colormap to style
    const saveStyle = () => {
        if (!style || !colormap) return;

        sldParser.readStyle(style).then(geoStyle => {
            geoStyle.output.rules[0].symbolizers[0].colorMap = colormap;
            const styleName = geoStyle.output.name.split('_')[0];

            const lastIndex = colormap.colorMapEntries.length - 1;
            let labelUnit = "";
            const lastQuantity = Number(getFloatNumberFromLabel(colormap.colorMapEntries[lastIndex].label, 1));

            if (styleName === MAXWVEL) {
                if (unitsType.current === "imperial") {
                    labelUnit = speedType.current === "knots" ? " kn" : " mph";
                } else {
                    labelUnit = "mps";
                }
            } else if (unitsType.current === "imperial") {
                labelUnit = " ft";
            } else {
                labelUnit = " m";
            }

            // check to see if this an intervals type of colormap
            // must handle weird last entry case, if so
            const lastEntry = geoStyle.output.rules[0].symbolizers[0].colorMap.colorMapEntries[lastIndex];
            lastEntry.label = colormap.type === "intervals" ? 
                    `>= ${lastQuantity.toFixed(2)}${labelUnit}`
                    :
                    `${lastQuantity.toFixed(2)}${labelUnit}`;

            const colorMapType = colormap.type;
            sldParser.writeStyle(geoStyle.output).then(sld => {
                const updatedStyle = restoreColorMapType(colorMapType, sld.output);
                switch (styleName) {
                case MAXELE: mapStyle.maxele.set(updatedStyle); break;
                case MAXWVEL: mapStyle.maxwvel.set(updatedStyle); break;
                case SWAN: mapStyle.swan.set(updatedStyle); break;
                }
            });
            });
    };

    const getProductType = () => {
        let type = MAXELE;
        if (style) {
            if (style.includes(MAXWVEL)) type = MAXWVEL;
            else if (style.includes(SWAN)) type = SWAN;
        }
        return type;
    };

    const productType = getProductType();

    return (
        <Stack direction="column" gap={2} alignItems="left">
        <Select placeholder="Choose Product Type To Style ..." onChange={handleProductChange}>
            {productList.map((product, index) => (
            <Option key={index} value={product}>{product}</Option>
            ))}
        </Select>

        {colormap &&
            <Stack direction="column" gap={1} alignItems="left">
            <Divider />
            <Box width={300}>
                <Typography level="title-md">Layer Opacity</Typography>
                <Slider
                aria-label="opacity slider"
                value={layerOpacity[productType].current}
                step={0.01}
                min={0.01}
                max={1.0}
                valueLabelDisplay="auto"
                sx={{ mr: '10px' }}
                onChange={(event, newValue) => layerOpacity[productType].set(newValue)}
                />
            </Box>
            <Divider />
            <Typography level="title-md">ColorMap</Typography>
            <ColorMapEditor
                colorMap={colormap}
                extendedField={{ visibility: false }}
                onChange={onColorMapChange}
            />
            <Button variant="soft" onClick={saveStyle} sx={{ width: "200px" }}>
                <Typography level="title-md">Save New Colormap</Typography>
            </Button>
            <Divider />
            </Stack>
        }
        </Stack>
    );
};